import { useMemo, useRef, useState } from 'react';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Sorter from '@/components/DataTable/Sorter';
import { useFeedbackTerminology } from '@/entities/workspace';
import type { DashboardDateFilterState } from '@/features/Dashboard/components/DashboardDateFilter';
import { FEEDBACK_SORT_OPTIONS } from '@/features/Feedback/configs/feedback-sort-options';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';

import {
  getDefaultFilterSet,
  getFeedbackGalleryTitle,
} from '../model/constants';
import {
  buildFilterFormatSignature,
  parseInitialFilterState,
} from '../model/parse-initial-filter-state';
import type { FeedbacksSettingT } from '../model/types';
import FeedbackGalleryView from './feedback-gallery-view';

type FeedbackGalleryEditProps = {
  feedbacksSetting: FeedbacksSettingT;
  columnId: string;
  onDone: (setting: FeedbacksSettingT) => void;
  onCancel: () => void;
  onDelete?: () => void;
  globalFilter?: StructuredFilter | null;
  globalDateFilter?: DashboardDateFilterState | null;
};

const FeedbackGalleryEdit = ({
  feedbacksSetting,
  columnId,
  onDone,
  onCancel,
  onDelete,
  globalFilter,
  globalDateFilter,
}: FeedbackGalleryEditProps) => {
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const { parseStructuredFilter } = useStructuredFilter();
  const [title, setTitle] = useState(
    getFeedbackGalleryTitle(feedbacksSetting.title, replaceFeedbackTerminology),
  );
  const [sortBy, setSortBy] = useState(feedbacksSetting.sort_by);
  const [orderBy, setOrderBy] = useState<OrderStatusT>(
    feedbacksSetting.order_by as OrderStatusT,
  );
  const [isFilterTouched, setIsFilterTouched] = useState(false);
  const hasUserInteractedWithFilterRef = useRef(false);

  const { initialFormats, editableFilterKeys } = useMemo(
    () =>
      parseInitialFilterState(
        feedbacksSetting.filter_set ?? {},
        parseStructuredFilter,
      ),
    [feedbacksSetting.filter_set, parseStructuredFilter],
  );
  const initialFilterSignature = useMemo(
    () => buildFilterFormatSignature(initialFormats),
    [initialFormats],
  );

  // StructuredFilter 상태 관리 (기존 필터 복원)
  const { filterQueryFormats, onFilterSetChange, clearFilter } =
    useStructuredFilterQuery(undefined, initialFormats);

  // StructuredFilter로 편집 가능한 필터 집합
  const editableFilterSet: StructuredFilter = useMemo(
    () => structuredFilterUtils.buildStructuredFilter(filterQueryFormats) ?? {},
    [filterQueryFormats],
  );

  // StructuredFilter에서 편집 불가능한 원본 필터는 유지 (예: 예외적인 커스텀 필터 키)
  const preservedFilterSet = useMemo(() => {
    const baseFilterSet = { ...(feedbacksSetting.filter_set ?? {}) };
    editableFilterKeys.forEach((key) => {
      delete baseFilterSet[key];
    });
    return baseFilterSet as StructuredFilter;
  }, [editableFilterKeys, feedbacksSetting.filter_set]);

  // 실시간 프리뷰/저장에 쓰이는 최종 필터
  const mergedFilterSet = useMemo(
    () =>
      ({
        ...preservedFilterSet,
        ...editableFilterSet,
      }) as StructuredFilter,
    [editableFilterSet, preservedFilterSet],
  );
  const activeFilterSet = useMemo(
    () =>
      isFilterTouched
        ? mergedFilterSet
        : (feedbacksSetting.filter_set ?? {}),
    [feedbacksSetting.filter_set, isFilterTouched, mergedFilterSet],
  );

  const previewSetting = useMemo<FeedbacksSettingT>(
    () => ({
      title,
      filter_set: activeFilterSet,
      sort_by: sortBy,
      order_by: orderBy,
    }),
    [activeFilterSet, orderBy, sortBy, title],
  );

  const handleDone = () => {
    const defaultFilterSet = getDefaultFilterSet();
    const startDate =
      (activeFilterSet[FEEDBACK_COLUMNS.DATE_START] as string | undefined) ??
      defaultFilterSet.start_date;
    const endDate =
      (activeFilterSet[FEEDBACK_COLUMNS.DATE_END] as string | undefined) ??
      defaultFilterSet.end_date;

    const normalizedWithoutDate = {
      ...activeFilterSet,
    } as StructuredFilter;
    delete normalizedWithoutDate[FEEDBACK_COLUMNS.DATE_START];
    delete normalizedWithoutDate[FEEDBACK_COLUMNS.DATE_END];

    const hasStructuredFilter = Object.keys(normalizedWithoutDate).length > 0;

    const normalizedFilterSet = hasStructuredFilter
      ? ({
          ...normalizedWithoutDate,
          [FEEDBACK_COLUMNS.DATE_START]: startDate,
          [FEEDBACK_COLUMNS.DATE_END]: endDate,
        } as StructuredFilter)
      : ({
          [FEEDBACK_COLUMNS.DATE_START]: startDate,
          [FEEDBACK_COLUMNS.DATE_END]: endDate,
        } as StructuredFilter);

    onDone({
      title,
      filter_set: normalizedFilterSet,
      sort_by: sortBy,
      order_by: orderBy,
    });
  };

  const handleSortChange = (sort: NotNullableSortStatusT) => {
    setSortBy(sort.sortBy);
    setOrderBy(sort.orderStatus);
  };

  return (
    <VFlex className="h-full overflow-hidden">
      <VFlex className="px-6 py-4" style={{ gap: 8 }}>
        {/* Title 편집 */}
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-base font-semibold outline-none focus:border-blue-300"
          placeholder="Gallery title"
        />

        {/* Filter + Sort */}
        <Flex className="items-center gap-2">
          <div
            onMouseDown={() => {
              hasUserInteractedWithFilterRef.current = true;
            }}
            onKeyDown={() => {
              hasUserInteractedWithFilterRef.current = true;
            }}
            onTouchStart={() => {
              hasUserInteractedWithFilterRef.current = true;
            }}
          >
            <StructuredFilter
              values={filterQueryFormats}
              onChange={(values) => {
                if (hasUserInteractedWithFilterRef.current) {
                  const nextSignature = buildFilterFormatSignature(values);
                  setIsFilterTouched(nextSignature !== initialFilterSignature);
                }
                onFilterSetChange(values);
              }}
              clearFilter={clearFilter}
            />
          </div>
          <Sorter
            options={FEEDBACK_SORT_OPTIONS}
            currSortStatus={{ sortBy, orderStatus: orderBy }}
            onSortChange={handleSortChange}
          />
        </Flex>
      </VFlex>

      {/* 실시간 프리뷰 */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <FeedbackGalleryView
          feedbacksSetting={previewSetting}
          columnId={columnId}
          globalFilter={globalFilter}
          globalDateFilter={globalDateFilter}
          showHeader={false}
        />
      </div>

      {/* Delete / Cancel / Done */}
      <Flex j="space-between" className="px-6 py-4">
        {onDelete ? (
          <Button variant="error_outlined" onClick={onDelete}>
            Delete
          </Button>
        ) : (
          <div />
        )}
        <Flex g={8} j="flex-end">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleDone}>Done</Button>
        </Flex>
      </Flex>
    </VFlex>
  );
};

export default FeedbackGalleryEdit;
